/**
 * Обёртка над IndexedDB.
 *
 * Своя, а не библиотека: используем два хранилища и десяток операций,
 * а idb тянет за собой поверхность, которая здесь не нужна. Бриф
 * прямо просит не плодить зависимости.
 *
 * Всё промисифицировано: сырой IndexedDB событийный, и забытый
 * обработчик onerror превращается в тихо повисший запрос.
 */

const DB_NAME = 'carrent-offline'
// v2: + chat_messages — кэш последних сообщений диалогов для чтения
// офлайн. Обновление аддитивно: существующие хранилища не трогаются.
const DB_VERSION = 2

export const STORE_PHOTOS = 'photos'
export const STORE_OUTBOX = 'outbox'
export const STORE_MESSAGES = 'chat_messages'

let dbPromise = null

/**
 * Схема.
 *
 * photos — снимки приёмки. Blob хранится целиком: File из input
 * живёт только пока жив компонент, и после перезагрузки страницы
 * восстановить его неоткуда.
 *
 * outbox — очередь отправки. Отдельно от фотографий: задача может
 * ссылаться на снимок, но живёт своей жизнью, и удаление одного
 * не должно каскадом убивать другое.
 */
function upgrade(db) {
  if (!db.objectStoreNames.contains(STORE_PHOTOS)) {
    const photos = db.createObjectStore(STORE_PHOTOS, { keyPath: 'id' })
    // Основная выборка: все снимки одного черновика.
    photos.createIndex('draft', ['draftId', 'phase'], { unique: false })
    // Для политики квот: что удалять первым.
    photos.createIndex('createdAt', 'createdAt', { unique: false })
  }

  if (!db.objectStoreNames.contains(STORE_OUTBOX)) {
    const outbox = db.createObjectStore(STORE_OUTBOX, { keyPath: 'id' })
    outbox.createIndex('status', 'status', { unique: false })
    // Воркер выбирает задачи, которым пора: индекс по времени
    // следующей попытки.
    outbox.createIndex('nextRetryAt', 'nextRetryAt', { unique: false })
    outbox.createIndex('draftId', 'draftId', { unique: false })
  }

  // chat_messages — кэш чтения: одна запись на диалог с последними
  // сообщениями. Одна запись, а не сообщение-на-строку: чтение
  // и запись атомарны, а больше последней страницы офлайн не нужно.
  if (!db.objectStoreNames.contains(STORE_MESSAGES)) {
    const messages = db.createObjectStore(STORE_MESSAGES, { keyPath: 'conversationId' })
    messages.createIndex('updatedAt', 'updatedAt', { unique: false })
  }
}

export function isSupported() {
  return typeof globalThis.indexedDB !== 'undefined'
}

export function open() {
  if (!isSupported()) return Promise.resolve(null)
  if (dbPromise) return dbPromise

  dbPromise = new Promise((resolve, reject) => {
    const request = globalThis.indexedDB.open(DB_NAME, DB_VERSION)
    request.onupgradeneeded = () => upgrade(request.result)
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
    // Другая вкладка держит старую версию открытой.
    request.onblocked = () => reject(new Error('База заблокирована другой вкладкой'))
  })

  return dbPromise
}

/** Сбросить кэш соединения. Нужен тестам и после удаления базы. */
export function reset() {
  dbPromise = null
}

/**
 * Закрыть соединение.
 *
 * Без этого deleteDatabase не выполняется, а тихо уходит в onblocked:
 * база остаётся, данные переживают «очистку», и тесты начинают
 * влиять друг на друга.
 */
export async function close() {
  if (!dbPromise) return
  try {
    const db = await dbPromise
    db?.close?.()
  } catch {
    /* соединение и так не открылось */
  }
  dbPromise = null
}

function run(storeName, mode, fn) {
  return open().then((db) => {
    if (!db) return null
    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, mode)
      const store = tx.objectStore(storeName)
      let result

      try {
        result = fn(store)
      } catch (err) {
        reject(err)
        return
      }

      // Ждём завершения транзакции, а не запроса: запись считается
      // сделанной только после коммита.
      //
      // Разворачиваем IDBRequest явно, а не через ??: у запроса,
      // не нашедшего запись, result равен undefined, и `?? result`
      // вернул бы сам объект запроса вместо пустого значения.
      tx.oncomplete = () => {
        if (result && typeof result === 'object' && 'result' in result) {
          resolve(result.result ?? null)
        } else {
          resolve(result ?? null)
        }
      }
      tx.onerror = () => reject(tx.error)
      tx.onabort = () => reject(tx.error || new Error('Транзакция прервана'))
    })
  })
}

export const put = (store, value) => run(store, 'readwrite', (s) => s.put(value))
export const get = (store, key) => run(store, 'readonly', (s) => s.get(key))
export const remove = (store, key) => run(store, 'readwrite', (s) => s.delete(key))
export const clear = (store) => run(store, 'readwrite', (s) => s.clear())
export const getAll = (store) => run(store, 'readonly', (s) => s.getAll())
export const count = (store) => run(store, 'readonly', (s) => s.count())

/** Выборка по индексу. */
export function getAllBy(store, indexName, query) {
  return run(store, 'readonly', (s) => s.index(indexName).getAll(query))
}

/** Записать несколько значений одной транзакцией. */
export function putAll(store, values) {
  return run(store, 'readwrite', (s) => {
    values.forEach((v) => s.put(v))
    return values.length
  })
}

/** Удалить базу целиком. Используется при отзыве согласия и в тестах. */
export async function destroy() {
  if (!isSupported()) return

  // Сначала опустошаем хранилища, потом удаляем базу.
  //
  // Одного deleteDatabase недостаточно: если соединение где-то ещё
  // открыто, запрос уходит в onblocked и тихо не выполняется —
  // база остаётся, а данные переживают «очистку».
  try {
    await clear(STORE_PHOTOS)
    await clear(STORE_OUTBOX)
  } catch {
    /* базы могло и не быть */
  }

  await close()

  await new Promise((resolve) => {
    const request = globalThis.indexedDB.deleteDatabase(DB_NAME)
    request.onsuccess = () => resolve()
    request.onerror = () => resolve()
    request.onblocked = () => resolve()
  })
}
